<template>
  <RootPageLayout>
    <template #header>
      <div class="flex w-full flex-col space-y-4">
        <div
          class="flex w-full items-center justify-between"
        >
          <PageTitle> Transactions </PageTitle>
          <BaseDatePickerDrawer v-model="dateFilter" />
        </div>
        <BaseButtonSwitch
          v-if="hasMultipleDevices"
          v-model="showAllTerminals"
          :options="terminalSwitchOptions"
        />
        <div>
          <SearchInput v-model="search" />
        </div>
      </div>
    </template>

    <div>
      <QueryLoading v-if="isLoading" />
      <QueryError v-else-if="isError">
        Failed to retrieve orders
      </QueryError>
      <template v-else-if="data">
        <template v-if="data.items.length > 0">
          <div class="mb-3">
            <template
              v-for="(
                [date, items], index
              ) in combinedItemsGroupedByDate"
              :key="date"
            >
              <div
                :class="[
                  'px-2 pb-3 text-sm',
                  index === 0 ? '' : 'pt-3',
                ]"
              >
                {{ date }}
              </div>
              <ol
                class="-mx-4 mb-6 space-y-px bg-background-fg/10 py-px"
              >
                <template
                  v-for="entry in items"
                  :key="
                    entry.kind === 'order'
                      ? `order-${entry.order.id}`
                      : `zreport-${entry.zReport.id}`
                  "
                >
                  <li v-if="entry.kind === 'order'">
                    <RouterLink
                      :to="{
                        name: 'orderDetail',
                        params: {
                          orderId: entry.order.id,
                        },
                      }"
                    >
                      <OrdersListItemOrder
                        :order="entry.order"
                      />
                    </RouterLink>
                  </li>
                  <li
                    v-else
                    class="cursor-pointer"
                    @click="
                      router.push({
                        name: 'settingsZReportDetail',
                        params: {
                          zReportId: entry.zReport.id,
                        },
                        state: {
                          backRoute: {
                            name: 'orderIndex',
                          },
                        },
                      })
                    "
                  >
                    <ZReportListItem
                      :report="entry.zReport"
                    />
                  </li>
                </template>
              </ol>
            </template>
          </div>
          <div class="flex justify-around px-8">
            <BasePagination
              v-model:page="page"
              :enable-next="enableNext"
              :enable-previous="enablePrevious"
            />
          </div>
        </template>

        <template v-else>
          <p
            class="w-full px-8 text-center text-background-fg/70"
          >
            No transactions to display
          </p>
        </template>
      </template>
    </div>
  </RootPageLayout>
</template>

<script setup lang="ts">
import {
  type TransactionsFeedCursor,
  type TransactionsFeedFilters,
  type TransactionsFeedItem,
  type TransactionsFeedQuery,
  useTransactionsFeed,
} from "@/services/orders/listTransactionsFeed"
import { debouncedRef } from "@vueuse/core"
import type { ButtonSwitchOption } from "@/components/BaseButtonSwitch.vue"

const dateFilter = ref<string | null>(null)
const showAllTerminals = ref(true)
const search = ref("")
const debouncedSearch = debouncedRef(search, 300)
const router = useRouter()
const { serialNumber } = useDevice()
const { hasMultipleDevices } = useTenant()

const filters = computed<TransactionsFeedFilters>(() => ({
  date: dateFilter.value || undefined,
  search: debouncedSearch.value.trim() || undefined,
  deviceSerialNumber: showAllTerminals.value
    ? undefined
    : serialNumber.value,
}))

const terminalSwitchOptions: ButtonSwitchOption[] = [
  { value: true, label: "All Terminals" },
  { value: false, label: "This Terminal" },
]

const {
  page,
  enableNext,
  enablePrevious,
  paginationQuery,
  resetPagination,
} = useCursorPagination<TransactionsFeedCursor>({
  initialCursor: {
    cursorAt: undefined,
    cursorPriority: undefined,
  },
  nextCursor: computed(() => ({
    cursorAt: data.value?.nextCursorAt,
    cursorPriority: data.value?.nextCursorPriority,
  })),
  enableNext: computed(() => data.value?.hasNext ?? false),
  limit: 10,
})

watch(
  () => filters.value,
  () => {
    resetPagination()
  },
  { deep: true, immediate: true }
)

const query = reactiveComputed<TransactionsFeedQuery>(
  () => ({
    ...filters.value,
    ...paginationQuery.value,
  })
)

const { data, isError, isLoading } =
  useTransactionsFeed(query)

const combinedItemsGroupedByDate = computed(() => {
  if (!data.value)
    return new Map<string, TransactionsFeedItem[]>()

  const groupedMap = new Map<
    string,
    TransactionsFeedItem[]
  >()

  for (const item of data.value.items) {
    const datePart = formatToDate(item.createdAt)
    const existing = groupedMap.get(datePart) || []
    groupedMap.set(datePart, [...existing, item])
  }

  return groupedMap
})
</script>
